import React from 'react';
import { Box, Text, Flex, VStack, useColorModeValue } from '@chakra-ui/react';

interface RiskManagementChartProps {
  lowRiskTasks: number;
  mediumRiskTasks: number;
  highRiskTasks: number;
  totalTasks: number;
  mode?: 'project' | 'channel';
  title?: string;
}

const RiskManagementChart: React.FC<RiskManagementChartProps> = ({
  lowRiskTasks,
  mediumRiskTasks,
  highRiskTasks,
  totalTasks,
  mode = 'project',
  title,
}) => {
  const cardBg = useColorModeValue('white', '#1A1C1F');
  const borderColor = useColorModeValue('#E5E7EB', 'rgba(39, 33, 33, 0.2)');
  const text = useColorModeValue('#111827', '#E5E7EB');
  const textMuted = useColorModeValue('#6B7280', '#C3C9D3');
  const lowColor = useColorModeValue('cyan.500', 'cyan.300');
  const medColor = useColorModeValue('blue.600', 'blue.300');
  const highColor = useColorModeValue('red.600', 'red.400');

  // Calculate bar widths with proportional scaling that never overflows.
  // Scale solely by the largest risk value so the biggest bar fills the chart.
  // If any bar would exceed, all bars are reduced proportionally by this same scale.
  const maxValue = Math.max(lowRiskTasks, mediumRiskTasks, highRiskTasks);
  const scale = Math.max(maxValue, 1);

  const computeWidthPercent = (value: number): number => {
    if (value <= 0) return 0;
    const percent = (value / scale) * 100;
    // Keep non-zero bars visible, and avoid touching the right border
    return Math.min(Math.max(percent, 12), 95);
  };

  const lowWidth = computeWidthPercent(lowRiskTasks);
  const mediumWidth = computeWidthPercent(mediumRiskTasks);
  const highWidth = computeWidthPercent(highRiskTasks);

  return (
    <Box className="rounded-lg" style={{ height: 200, width: '100%' }} bg={cardBg} border="1px solid" borderColor={borderColor}>
      <Flex direction="column" align="stretch" p={4} pt={8} w="100%">
        {/* Chart Area */}
        <Flex direction="row" align="flex-end" className="mb-2" ml={4} w="100%">
          {/* Y-axis Labels */}
          <VStack spacing={4} align={'flex-start'} className="mr-4" w="80px">
            <Text className="text-sm font-medium ml={0}" color={text}>Low</Text>
            <Text className="text-sm font-medium ml={0}" color={text}>Medium</Text>
            <Text className="text-sm font-medium ml={0}" color={text}>High</Text>
          </VStack>

          {/* Bars */}
          <VStack spacing={2} align="stretch" flex="1">
            {/* Low Risk Bar */}
            <Flex align="center">
              <Box
                bg={lowColor}
                h="25px"
                w={`${lowWidth}%`}
                rounded="sm"
                mr={2}
                ml={0}
                transition="width 0.3s ease"
              />
              <Text className="text-sm font-medium" color={text}>{lowRiskTasks}</Text>
            </Flex>

            {/* Medium Risk Bar */}
            <Flex align="center">
              <Box
                bg={medColor}
                h="25px"
                w={`${mediumWidth}%`}
                rounded="sm"
                mr={2}
                ml={0}
                transition="width 0.3s ease"
              />
              <Text className="text-sm font-medium" color={text}>{mediumRiskTasks}</Text>
            </Flex>

            {/* High Risk Bar */}
            <Flex align="center">
              <Box
                bg={highColor}
                h="25px"
                w={`${highWidth}%`}
                rounded="sm"
                mr={2}
                ml={0}
                transition="width 0.3s ease"
              />
              <Text className="text-sm font-medium" color={text}>{highRiskTasks}</Text>
            </Flex>
          </VStack>
        </Flex>

        {/* Legend - Centered horizontally */}
        <Flex justify="center" mt={10}>
          <Flex align="center" mr={4}>
            <Box w="12px" h="12px" bg={lowColor} rounded="full" mr={1} />
            <Text className="text-sm" color={textMuted}>Low</Text>
          </Flex>
          <Flex align="center" mr={4}>
            <Box w="12px" h="12px" bg={medColor} rounded="full" mr={1} />
            <Text className="text-sm" color={textMuted}>Medium</Text>
          </Flex>
          <Flex align="center">
            <Box w="12px" h="12px" bg={highColor} rounded="full" mr={1} />
            <Text className="text-sm" color={textMuted}>High</Text>
          </Flex>
        </Flex>

        {/* Removed X-axis Labels as per user request */}
      </Flex>
    </Box>
  );
};

export default RiskManagementChart; 